import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ClientLayout from "../../../components/client/ClientLayout";
import PasswordInput from "../../../components/PasswordInput";
import { clientApi } from "../../../api";
import { formatDateAU } from "../../../utils/formatDate";

/**
 * IGT Verify PIN — counter tool for "this customer wants a PIN-gated
 * benefit (redemption, tier unlock, etc.)".
 *
 * Mirrors S7000's PinLookup shape so staff using both providers don't
 * have to retrain. The backend (`POST /api/client/igt/verify-pin`) is
 * provider-specific — IGT has no card-based validate_pin, so a card
 * input is resolved to a membership number first, then the PIN is
 * validated. On success we also pull restrictions inline so a banned
 * member gets flagged up-front.
 */
const REASON_TEXT = {
  incorrect_pin:      "Incorrect PIN. Please try again.",
  encryption_error:   "Security check failed. Please try again.",
  nonce_reused:       "Please wait a moment and try again.",
  prefix_reused:      "Please wait a moment and try again.",
  invalid_pin_length: "PIN length is invalid for this venue.",
  member_not_found:   "No member found with that card or membership number.",
};

export default function IgtVerifyPin() {
  const navigate = useNavigate();
  const cardRef  = useRef(null);

  const [cardNumber, setCardNumber]     = useState("");
  const [memberNumber, setMemberNumber] = useState("");
  const [pin, setPin]                   = useState("");
  const [busy, setBusy]                 = useState(false);
  const [result, setResult]             = useState(null);   // { valid, member, restrictions }
  const [error, setError]               = useState("");

  async function verify(e) {
    e.preventDefault();
    setError(""); setResult(null);
    if (!cardNumber && !memberNumber) {
      setError("Enter a card number or membership number.");
      return;
    }
    if (!/^\d{3,10}$/.test(pin)) {
      setError("PIN must be 3–10 digits.");
      return;
    }
    setBusy(true);
    try {
      const r = await clientApi.igt.verifyPin({
        cardNumber:   cardNumber.trim() || undefined,
        memberNumber: memberNumber.trim() || undefined,
        pin,
      });
      if (r.valid) {
        setResult(r);
      } else {
        setError(REASON_TEXT[r.reason] || "PIN verification failed.");
        setPin("");   // keep identifiers, reset PIN only
      }
    } catch (err) {
      setError(err?.message || "PIN verification failed.");
    }
    setBusy(false);
  }

  function clearAll() {
    setCardNumber(""); setMemberNumber(""); setPin(""); setResult(null); setError("");
    setTimeout(() => cardRef.current?.focus(), 0);
  }

  const m = result?.member || null;
  const restrictionBanner = (() => {
    const r = result?.restrictions;
    if (!r) return null;
    if (r.isBanned)       return { kind: "danger", text: "⚠ Banned — this member cannot use the membership.", reasons: r.reasons };
    if (r.isSelfExcluded) return { kind: "danger", text: "⚠ Self-exclusion on file — contact the venue.",     reasons: r.reasons };
    return null;
  })();

  return (
    <ClientLayout>
      <div className="venues-page-header">
        <div>
          <h1>Verify member PIN</h1>
          <p className="page-subtitle">IGT — confirm identity at the counter for PIN-gated benefits</p>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 440px) minmax(0, 1fr)", gap: "1.5rem", alignItems: "start" }}>
        <form onSubmit={verify} className="settings-card">
          <div className="settings-card-header">
            <h3>Identify member</h3>
            <p>Scan or type card, or enter membership number + PIN.</p>
          </div>

          {error && <div className="toast toast-error" style={{ marginBottom: "0.75rem" }}>{error}</div>}

          <div className="form-field">
            <label>Card number</label>
            <input
              ref={cardRef}
              value={cardNumber}
              onChange={(e) => setCardNumber(e.target.value.replace(/\s+/g, ""))}
              placeholder="Scan card"
              autoFocus
            />
          </div>

          <div style={{ textAlign: "center", color: "var(--text-muted)", fontSize: "0.8rem", margin: "0.25rem 0" }}>or</div>

          <div className="form-field">
            <label>Membership number</label>
            <input
              value={memberNumber}
              onChange={(e) => setMemberNumber(e.target.value.replace(/\D/g, ""))}
              placeholder="e.g. 56153 or QR contents"
              inputMode="numeric"
            />
          </div>

          <div className="form-field">
            <label>PIN <span className="required">*</span></label>
            <PasswordInput
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
              maxLength={10}
              inputMode="numeric"
              pattern="\d*"
              required
              placeholder="3–10 digits"
            />
          </div>

          <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.5rem" }}>
            <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? "Checking…" : "Verify"}</button>
            <button type="button" className="btn btn-outline" onClick={clearAll}>Clear</button>
          </div>
        </form>

        <div>
          {!result ? (
            <div className="empty-state" style={{ borderStyle: "dashed" }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="48" height="48">
                <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/>
              </svg>
              <h3>Awaiting verification</h3>
              <p>Nothing is shown until the PIN is confirmed — prevents identity leak on wrong PIN.</p>
            </div>
          ) : (
            <div className="settings-card">
              {restrictionBanner && (
                <div className={`toast ${restrictionBanner.kind === "danger" ? "toast-error" : ""}`} style={{ marginBottom: "1rem" }}>
                  {restrictionBanner.text}
                  {restrictionBanner.reasons?.length > 0 && (
                    <div style={{ fontSize: "0.8rem", opacity: 0.85, marginTop: "0.25rem" }}>
                      {restrictionBanner.reasons.join(", ")}
                    </div>
                  )}
                </div>
              )}

              <div style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "1rem" }}>
                <div style={{
                  width: 56, height: 56, borderRadius: 12,
                  background: "var(--success-bg, #10b98122)", color: "var(--success, #10b981)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="24" height="24"><polyline points="20 6 9 17 4 12"/></svg>
                </div>
                <div>
                  <div style={{ fontSize: "1.2rem", fontWeight: 700 }}>Verified</div>
                  <div style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>PIN matches MeRS</div>
                </div>
              </div>

              {m && (
                <>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "0.5rem" }}>
                    <Field label="Name"          value={[m.firstName, m.lastName].filter(Boolean).join(" ") || "—"} />
                    <Field label="Membership #"  value={m.membershipNumber ? `#${m.membershipNumber}` : "—"} />
                    <Field label="Card #"        value={m.cardNumber || cardNumber || "—"} />
                    <Field label="Status"        value={m.membershipStatus || "—"} />
                    <Field label="Date of birth" value={m.dateOfBirth ? formatDateAU(m.dateOfBirth) : "—"} />
                    <Field label="Email"         value={m.email || "—"} />
                    <Field label="Mobile"        value={m.mobile || "—"} />
                  </div>

                  {m.address && (
                    <div style={{ marginTop: "1rem", padding: "0.75rem", border: "1px solid var(--border)", borderRadius: 8 }}>
                      <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginBottom: "0.3rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>Address</div>
                      <div style={{ fontSize: "0.9rem" }}>
                        {[m.address.line1, m.address.line2, m.address.city, m.address.state, m.address.postcode].filter(Boolean).join(", ") || "—"}
                      </div>
                    </div>
                  )}
                </>
              )}

              <div style={{ display: "flex", gap: "0.5rem", marginTop: "1rem", flexWrap: "wrap" }}>
                {m?.membershipNumber && (
                  <button className="btn btn-primary" onClick={() => navigate(`/client/members/${m.membershipNumber}`)}>
                    Open full profile →
                  </button>
                )}
                <button className="btn btn-outline" onClick={clearAll}>Next member</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </ClientLayout>
  );
}

function Field({ label, value }) {
  return (
    <div className="stat-card" style={{ padding: "0.6rem 0.8rem" }}>
      <span className="stat-label">{label}</span>
      <span style={{ fontSize: "0.9rem", color: "var(--text)", marginTop: "0.25rem" }}>{value}</span>
    </div>
  );
}
